import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { applicationAPI } from '../../services/api';
import {
  PHONE_ERROR,
  isTenDigitPhone,
  normalizePhone,
  validateFileSelection,
  validateLicenseEntries,
  validateTrainingEntries,
  validateMartialArtsEntries,
  validateLanguageEntries,
  firstErrorIndex
} from '../../utils/applicationValidation';
import {
  loadDraft,
  saveDraft,
  clearDraft,
  pruneSensitive,
  mergeDeep,
  debounce
} from '../../utils/applicationDraft';
import {
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/react/outline';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { LANGUAGES, LICENSE_TYPES, US_STATES, VALIDATION_RULES } from '../../utils/constants';
import { captureReferralFromUrl, loadStoredReferral, REF_CODE_RE } from '../../utils/referral';
import { QRCodeSVG } from 'qrcode.react';
import ApplicationReview from './ApplicationReview';
import ApplicationIntro from './ApplicationIntro';
import ApplicationResume from './ApplicationResume';
import LanguageSwitcher from '../common/LanguageSwitcher';

const EMPTY_LANGUAGE = { language: '', otherLanguage: '', proficiency: 'basic' };
const EMPTY_LICENSE = { type: '', otherType: '', number: '', issuingAuthority: '', otherAuthority: '', issueDate: '', expiryDate: '' };
const EMPTY_TRAINING = { courseName: '', institution: '', completionDate: '' };
const EMPTY_MARTIAL_ART = { style: '', level: '', yearsExperience: 0 };

// Registered fields validated when leaving each step (step 2 only carries
// dynamic entry lists, which are validated separately).
const STEP_FIELDS = {
  1: [
    'firstName', 'lastName', 'email', 'phone',
    'personalInfo.address.street', 'personalInfo.address.city',
    'personalInfo.address.state', 'personalInfo.address.zip',
    'personalInfo.dateOfBirth', 'physicalAttributes.gender'
  ],
  3: ['experience.yearsOfExperience', 'backgroundCheck.consent', 'insurance.hasLiabilityInsurance', 'availability.startDate'],
};

// Translation keys, resolved at render so the stepper relabels when the
// applicant switches language mid-form.
const STEP_LABEL_KEYS = ['steps.contact', 'steps.licenses', 'steps.skills', 'steps.documents', 'steps.review'];

// Pulled out of the component so "start fresh" can reset to exactly the same
// shape a first-time applicant gets. A referral code captured from ?ref=
// survives the reset — discarding a draft shouldn't cost the referrer credit.
const buildDefaultValues = (referralCode) => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  referralCode: referralCode || '',
  personalInfo: {
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    dateOfBirth: '',
    militaryService: {
      served: false,
      branch: '',
      rank: '',
      yearsServed: 0,
      honorableDischarge: false
    }
  },
  physicalAttributes: {
    gender: ''
  },
  backgroundCheck: {
    consent: false,
    criminalRecord: {
      hasCriminalRecord: false,
      details: ''
    }
  },
  insurance: {
    hasLiabilityInsurance: ''
  },
  additionalInfo: '',
  skills: {
    drivingLicense: {
      hasLicense: false,
      classes: [],
      defensiveDriving: false
    },
    firstAid: {
      certified: false,
      certificationDate: '',
      provider: ''
    },
    weaponsTraining: {
      trained: false,
      permitNumber: '',
      types: []
    },
    martialArtsTrained: false
  },
  availability: {
    startDate: new Date().toISOString().split('T')[0],
    fullTime: false,
    partTime: false,
    willingToTravel: false,
    preferredAreas: [],
    nightShifts: false,
    weekends: false
  },
  experience: {
    yearsOfExperience: 0,
    specializations: ''
  }
});

const ApplicationForm = () => {
  const { t, i18n } = useTranslation();
  // Saved draft (if any) from a previous visit — loaded once.
  const [draft] = useState(loadDraft);
  // Capture ?ref=CODE from the URL (first touch, 90 days), then load whatever
  // code is stored so it can prefill the optional referral field.
  const [storedReferral] = useState(() => {
    captureReferralFromUrl();
    return loadStoredReferral();
  });
  const [loading, setLoading] = useState(false);
  // New applicants see the intro screen first. A saved draft used to resume
  // silently, which meant a browser shared with someone else showed their
  // half-filled answers unannounced — so a draft now asks first (see
  // showResume) and the intro is skipped only once resuming is chosen.
  const [showIntro, setShowIntro] = useState(!draft);
  // A saved draft exists: ask whether to resume it or start over.
  const [showResume, setShowResume] = useState(!!draft);
  // Clamp restored steps: older drafts were saved against a 6-step layout, and
  // loadDraft only checks that step is finite — not that it is in range. The
  // resume screen shows this number, so a corrupt draft must not render
  // "step -3 of 5".
  const [currentStep, setCurrentStep] = useState(
    Math.min(Math.max(draft?.step || 1, 1), 5)
  );
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [myReferralCode, setMyReferralCode] = useState(null);
  const [entryErrors, setEntryErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const submittedRef = useRef(false);

  // File state: single file for id, arrays for the rest.
  // Files can't be persisted in a draft — they must be re-attached.
  const [files, setFiles] = useState({
    id: null,
    license: [],
    certification: [],
    other: []
  });

  // A code captured from ?ref= has to survive a draft that was saved before the
  // link was ever clicked. mergeDeep puts the draft last, so the draft's empty
  // referralCode would otherwise silently overwrite — and discard — the
  // attribution. A code the applicant typed themselves still wins.
  const draftValues = useMemo(() => {
    const values = { ...(draft?.values || {}) };
    if (storedReferral && !String(values.referralCode || '').trim()) {
      values.referralCode = storedReferral;
    }
    return values;
  }, [draft, storedReferral]);

  const [languages, setLanguages] = useState(draft?.languages?.length ? draft.languages : [{ ...EMPTY_LANGUAGE }]);
  const [licenses, setLicenses] = useState(draft?.licenses?.length ? draft.licenses : [{ ...EMPTY_LICENSE }]);
  const [training, setTraining] = useState(draft?.training?.length ? draft.training : [{ ...EMPTY_TRAINING }]);
  const [martialArts, setMartialArts] = useState(draft?.martialArts?.length ? draft.martialArts : [{ ...EMPTY_MARTIAL_ART }]);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: mergeDeep(buildDefaultValues(storedReferral), draftValues)
  });

  const militaryServed = watch('personalInfo.militaryService.served');
  const hasCriminalRecord = watch('backgroundCheck.criminalRecord.hasCriminalRecord');
  const insuranceAnswer = watch('insurance.hasLiabilityInsurance');
  const hasDriversLicense = watch('skills.drivingLicense.hasLicense');
  const isFirstAidCertified = watch('skills.firstAid.certified');
  const hasWeaponsTraining = watch('skills.weaponsTraining.trained');
  const hasMartialArtsTraining = watch('skills.martialArtsTrained');
  const referralValue = watch('referralCode');
  const formValues = watch();

  // Confirm the code back to the applicant only when it arrived via ?ref= and is
  // still untouched in the field. The value is user-controlled URL input, so it
  // is gated on REF_CODE_RE here and rendered as a plain JSX text node below —
  // never through i18next interpolation (escapeValue is false app-wide) and
  // never as HTML. At most 8 characters of [A-HJ-NP-Z2-9] can reach the DOM.
  const appliedReferral = useMemo(() => {
    const current = String(referralValue || '').trim().toUpperCase();
    if (!current || current !== storedReferral) return null;
    return REF_CODE_RE.test(current) ? current : null;
  }, [referralValue, storedReferral]);

  const totalSteps = 5;
  const today = new Date().toISOString().split('T')[0];
  // Set when the review step mounts; used to swallow the ghost second click
  // of a double-click on "Next" that would otherwise land on the Submit
  // button rendered in the same spot.
  const reviewEnteredAtRef = useRef(0);

  // Autosave a draft (minus government ID and criminal-record details) so a
  // refresh or a "come back later" doesn't lose the applicant's progress.
  const persistDraft = useMemo(
    () => debounce((snapshot) => {
      if (!submittedRef.current) saveDraft(snapshot);
    }, 800),
    []
  );

  useEffect(() => {
    // Don't save a draft while the intro screen is showing — otherwise a
    // visitor who only read the intro would skip it on their next visit.
    // Nor while the resume prompt is up: saving there would rewrite the very
    // draft the visitor is being asked about, and could resurrect one they
    // just chose to discard.
    if (showIntro || showResume) return;
    persistDraft({
      step: currentStep,
      values: pruneSensitive(formValues),
      languages,
      licenses,
      training,
      martialArts,
    });
  }, [persistDraft, showIntro, showResume, formValues, currentStep, languages, licenses, training, martialArts]);

  // Resume the saved draft. The confirmation toast fires here rather than on
  // mount so it follows the applicant's own choice instead of announcing a
  // restore they were never asked about.
  const handleResume = () => {
    setShowResume(false);
    toast(t('toast.draftRestored'), { icon: '💾', duration: 6000 });
  };

  // Discard the draft and begin again, resetting every piece of state that was
  // seeded from it — the form values, the four dynamic entry lists, any
  // attached files, and the step. The captured referral code is preserved.
  const handleStartFresh = () => {
    clearDraft();
    reset(buildDefaultValues(storedReferral));
    setLanguages([{ ...EMPTY_LANGUAGE }]);
    setLicenses([{ ...EMPTY_LICENSE }]);
    setTraining([{ ...EMPTY_TRAINING }]);
    setMartialArts([{ ...EMPTY_MARTIAL_ART }]);
    setFiles({ id: null, license: [], certification: [], other: [] });
    setEntryErrors({});
    setSubmitError(null);
    setCurrentStep(1);
    setShowResume(false);
    setShowIntro(true);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (currentStep === totalSteps) {
      reviewEnteredAtRef.current = Date.now();
    }
  }, [currentStep, totalSteps]);

  // Re-validate flagged entry lists as the applicant types so fixed rows clear.
  useEffect(() => {
    setEntryErrors(prev => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      if (prev.licenses) next.licenses = validateLicenseEntries(licenses);
      if (prev.training) next.training = validateTrainingEntries(training);
      if (prev.languages) next.languages = validateLanguageEntries(languages);
      if (prev.martialArts) next.martialArts = validateMartialArtsEntries(martialArts);
      return next;
    });
  }, [licenses, training, languages, martialArts]);

  // File handling helpers
  const handleFileChange = (e, type) => {
    // Snapshot the selected files NOW into a plain array. The state updater below
    // runs asynchronously, after we reset e.target.value = '' — which empties the
    // live FileList. Reading it lazily inside the updater would yield nothing
    // (this is why certification/license never saved).
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;

    // Hard-block bad files here, with the same rules the server enforces —
    // the accept= attribute only filters the picker dialog.
    const { ok, errors: fileErrors } = validateFileSelection(type, selected, files);
    if (!ok) {
      // Validators return { key, params }. `allowed` is itself a key (the list
      // of accepted extensions), so it is resolved before interpolation.
      fileErrors.forEach(({ key, params }) =>
        toast.error(
          t(key, {
            ...params,
            ...(params?.allowed ? { allowed: t(params.allowed) } : {}),
          })
        )
      );
      e.target.value = '';
      return;
    }
    setSubmitError(null);

    if (type === 'id') {
      setFiles(prev => ({ ...prev, [type]: selected[0] }));
    } else {
      setFiles(prev => ({ ...prev, [type]: [...prev[type], ...selected] }));
    }

    // Reset so re-selecting the same file still fires onChange.
    e.target.value = '';
  };

  const removeFile = (type, index) => {
    if (type === 'id') {
      setFiles(prev => ({ ...prev, [type]: null }));
    } else {
      setFiles(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== index) }));
    }
  };

  // Dynamic list handlers
  const addLanguage = () => {
    setLanguages([...languages, { ...EMPTY_LANGUAGE }]);
  };

  const removeLanguage = (index) => {
    if (languages.length > 1) {
      setLanguages(languages.filter((_, i) => i !== index));
    }
  };

  const addLicense = () => {
    setLicenses([...licenses, { ...EMPTY_LICENSE }]);
  };

  const removeLicense = (index) => {
    if (licenses.length > 1) {
      setLicenses(licenses.filter((_, i) => i !== index));
    }
  };

  const addTraining = () => {
    setTraining([...training, { ...EMPTY_TRAINING }]);
  };

  const removeTraining = (index) => {
    if (training.length > 1) {
      setTraining(training.filter((_, i) => i !== index));
    }
  };

  const addMartialArt = () => {
    setMartialArts([...martialArts, { ...EMPTY_MARTIAL_ART }]);
  };

  const removeMartialArt = (index) => {
    if (martialArts.length > 1) {
      setMartialArts(martialArts.filter((_, i) => i !== index));
    }
  };

  // Dynamic entry lists per step. Entries someone started filling in must be
  // completed (or removed) — previously incomplete rows were silently dropped
  // at submit, so applicants could believe they had submitted a license.
  const stepArrayChecks = (step) => {
    const checks = {};
    if (step === 2 || step === null) {
      checks.licenses = { step: 2, errors: validateLicenseEntries(licenses) };
      checks.training = { step: 2, errors: validateTrainingEntries(training) };
    }
    if (step === 3 || step === null) {
      checks.languages = { step: 3, errors: validateLanguageEntries(languages) };
      checks.martialArts = { step: 3, errors: validateMartialArtsEntries(martialArts) };
    }
    return checks;
  };

  const applyArrayChecks = (checks) => {
    const flagged = Object.entries(checks).filter(([, c]) => firstErrorIndex(c.errors) !== -1);
    setEntryErrors(prev => ({
      ...prev,
      ...Object.fromEntries(Object.entries(checks).map(([k, c]) => [k, c.errors]))
    }));
    return flagged.length ? Math.min(...flagged.map(([, c]) => c.step)) : null;
  };

  const handleNext = async () => {
    const names = STEP_FIELDS[currentStep];
    const fieldsOk = names ? await trigger(names) : true;
    const badStep = applyArrayChecks(stepArrayChecks(currentStep));
    if (!fieldsOk || badStep !== null) {
      toast.error(t('toast.fixHighlighted'));
      return;
    }
    // The required document gates the review step, not just final submission.
    if (currentStep === 4 && !files.id) {
      toast.error(t('toast.idRequired'));
      return;
    }
    setSubmitError(null);
    setCurrentStep(currentStep + 1);
  };

  const onSubmit = async (data) => {
    // Pressing Enter in a field submits the form on any step; treat it as Next.
    if (currentStep < totalSteps) {
      await handleNext();
      return;
    }

    // Swallow submissions that arrive almost immediately after the review step
    // mounts: the second click of a double-click on "Next" lands on the Submit
    // button that renders in the same position (the "review flashed and it
    // submitted itself" bug).
    if (Date.now() - reviewEnteredAtRef.current < 800) {
      return;
    }

    // Final gate: re-check every dynamic list (a restored draft can land here
    // without passing through earlier steps) and jump to the first bad step.
    const badStep = applyArrayChecks(stepArrayChecks(null));
    if (badStep !== null) {
      setCurrentStep(badStep);
      toast.error(t('toast.fixEntries'));
      return;
    }

    // Client validation for the required file
    if (!files.id) {
      toast.error(t('toast.idRequired'));
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      // Prepare application data. fullName stays as the combined value so the
      // backend, DynamoDB records and admin dashboard keep working unchanged.
      const firstName = (data.firstName || '').trim();
      const lastName = (data.lastName || '').trim();
      const applicationData = {
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        email: data.email,
        phone: normalizePhone(data.phone),
        referralCode: (data.referralCode || '').trim().toUpperCase() || undefined,
        physicalAttributes: data.physicalAttributes || {},
        experience: {
          yearsOfExperience: parseInt(data.experience.yearsOfExperience) || 0,
          specializations: data.experience.specializations
            ? data.experience.specializations.split(',').map(s => s.trim())
            : []
        },
        personalInfo: data.personalInfo,
        licenses: licenses
          // Military/LE entries qualify by type alone; everything else needs a number.
          .filter(l => l.type && (l.number || l.type.startsWith('Military/Law Enforcement')))
          .map(l => ({
            type: l.type === 'other' && l.otherType ? l.otherType.trim() : l.type,
            number: l.number,
            issuingAuthority: l.issuingAuthority === 'other' && l.otherAuthority
              ? l.otherAuthority.trim()
              : l.issuingAuthority,
            issueDate: l.issueDate,
            expiryDate: l.expiryDate
          })),
        training: training.filter(t => t.courseName),
        backgroundCheck: data.backgroundCheck,
        insurance: {
          hasLiabilityInsurance: data.insurance?.hasLiabilityInsurance === 'yes'
        },
        additionalInfo: (data.additionalInfo || '').trim(),
        // The language this application was actually completed in. Reviewers
        // need it to know which language to answer in — every other field is
        // stored canonically in English, so nothing else reveals it.
        locale: i18n.resolvedLanguage || i18n.language || 'en',
        skills: {
          ...data.skills,
          languages: languages
            .filter(l => l.language)
            .map(l => ({
              language: l.language === 'other' && l.otherLanguage ? l.otherLanguage.trim() : l.language,
              proficiency: l.proficiency
            })),
          martialArts: data.skills.martialArtsTrained ? martialArts.filter(m => m.style) : []
        },
        availability: data.availability,
      };

      const fd = new FormData();
      fd.append('application', JSON.stringify(applicationData));

      if (files.id) fd.append('id', files.id);

      files.license.forEach(f => fd.append('license', f));
      files.certification.forEach(f => fd.append('certification', f));
      files.other.forEach(f => fd.append('other', f));

      const res = await applicationAPI.submit(fd);

      submittedRef.current = true;
      clearDraft();
      setSubmitted(true);
      setApplicationId(res.data.applicationId);
      setMyReferralCode(res.data.referralCode || null);
      toast.success(t('toast.submitted'));
    } catch (error) {
      console.error('Application submission error:', error.response?.data);
      // Server messages are English-only; the generic fallback is translated so
      // the common failure (network/timeout) reads in the applicant's language.
      const message = error.response?.data?.message || t('toast.submitFailed');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-xl font-semibold text-gray-900">{t('contact.heading')}</h2>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{t('contact.firstName')} *</label>
                <input
                  type="text"
                  aria-label={t('contact.firstName')}
                  autoComplete="given-name"
                  className={`form-input ${errors.firstName ? 'border-red-500' : ''}`}
                  {...register('firstName', { required: 'validation.firstNameRequired' })}
                />
                {errors.firstName && (
                  <p className="form-error" role="alert">{t(errors.firstName.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.lastName')} *</label>
                <input
                  type="text"
                  aria-label={t('contact.lastName')}
                  autoComplete="family-name"
                  className={`form-input ${errors.lastName ? 'border-red-500' : ''}`}
                  {...register('lastName', { required: 'validation.lastNameRequired' })}
                />
                {errors.lastName && (
                  <p className="form-error" role="alert">{t(errors.lastName.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.email')} *</label>
                <input
                  type="email"
                  aria-label={t('contact.email')}
                  className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                  {...register('email', {
                    required: 'validation.emailRequired',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'validation.emailRequired'
                    }
                  })}
                />
                {errors.email && (
                  <p className="form-error" role="alert">{t(errors.email.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.phone')} *</label>
                <input
                  type="tel"
                  inputMode="tel"
                  aria-label={t('contact.phone')}
                  placeholder={t('contact.phonePlaceholder')}
                  className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                  {...register('phone', {
                    required: PHONE_ERROR,
                    validate: (v) => isTenDigitPhone(v) || PHONE_ERROR
                  })}
                />
                {errors.phone && (
                  <p className="form-error" role="alert">{t(errors.phone.message)}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="form-label">{t('contact.street')} *</label>
                <input
                  type="text"
                  aria-label={t('contact.street')}
                  autoComplete="street-address"
                  className={`form-input ${errors.personalInfo?.address?.street ? 'border-red-500' : ''}`}
                  {...register('personalInfo.address.street', { required: 'validation.streetRequired' })}
                />
                {errors.personalInfo?.address?.street && (
                  <p className="form-error" role="alert">{t(errors.personalInfo.address.street.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.city')} *</label>
                <input
                  type="text"
                  aria-label={t('contact.city')}
                  autoComplete="address-level2"
                  className={`form-input ${errors.personalInfo?.address?.city ? 'border-red-500' : ''}`}
                  {...register('personalInfo.address.city', { required: 'validation.cityRequired' })}
                />
                {errors.personalInfo?.address?.city && (
                  <p className="form-error" role="alert">{t(errors.personalInfo.address.city.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.state')} *</label>
                <select
                  aria-label={t('contact.state')}
                  autoComplete="address-level1"
                  className={`form-input ${errors.personalInfo?.address?.state ? 'border-red-500' : ''}`}
                  {...register('personalInfo.address.state', { required: 'validation.stateRequired' })}
                >
                  <option value="">{t('contact.selectState')}</option>
                  {/* US state names are proper nouns and are also the submitted
                      value, so they stay as-is in every language. */}
                  {US_STATES.map(s => (
                    <option key={s.value} value={s.label}>{s.label}</option>
                  ))}
                </select>
                {errors.personalInfo?.address?.state && (
                  <p className="form-error" role="alert">{t(errors.personalInfo.address.state.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.zip')} *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  aria-label={t('contact.zip')}
                  autoComplete="postal-code"
                  className={`form-input ${errors.personalInfo?.address?.zip ? 'border-red-500' : ''}`}
                  {...register('personalInfo.address.zip', {
                    required: 'validation.zipRequired',
                    pattern: { value: VALIDATION_RULES.ZIP_CODE, message: 'validation.zipInvalid' }
                  })}
                />
                {errors.personalInfo?.address?.zip && (
                  <p className="form-error" role="alert">{t(errors.personalInfo.address.zip.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.dateOfBirth')} *</label>
                <input
                  type="date"
                  aria-label={t('contact.dateOfBirth')}
                  autoComplete="bday"
                  max={today}
                  className={`form-input ${errors.personalInfo?.dateOfBirth ? 'border-red-500' : ''}`}
                  {...register('personalInfo.dateOfBirth', {
                    required: 'validation.dobRequired',
                    validate: (v) => {
                      const dob = new Date(v);
                      if (Number.isNaN(dob.getTime())) return 'validation.dobRequired';
                      const now = new Date();
                      let age = now.getFullYear() - dob.getFullYear();
                      const monthDiff = now.getMonth() - dob.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
                        age -= 1;
                      }
                      return age >= 18 || 'validation.dobTooYoung';
                    }
                  })}
                />
                {errors.personalInfo?.dateOfBirth && (
                  <p className="form-error" role="alert">{t(errors.personalInfo.dateOfBirth.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label">{t('contact.gender')} *</label>
                <select
                  aria-label={t('contact.gender')}
                  className={`form-input ${errors.physicalAttributes?.gender ? 'border-red-500' : ''}`}
                  {...register('physicalAttributes.gender', { required: 'validation.genderRequired' })}
                >
                  <option value="">{t('contact.selectGender')}</option>
                  <option value="male">{t('options.gender.male')}</option>
                  <option value="female">{t('options.gender.female')}</option>
                  <option value="other">{t('options.gender.other')}</option>
                  <option value="prefer_not_to_say">{t('options.gender.prefer_not_to_say')}</option>
                </select>
                {errors.physicalAttributes?.gender && (
                  <p className="form-error" role="alert">{t(errors.physicalAttributes.gender.message)}</p>
                )}
              </div>

              <div>
                <label className="form-label" htmlFor="referralCode">{t('contact.referralCode')}</label>
                <input
                  id="referralCode"
                  type="text"
                  aria-label={t('contact.referralCode')}
                  aria-describedby="referralCode-help"
                  maxLength={8}
                  placeholder={t('contact.referralPlaceholder')}
                  className={`form-input uppercase ${
                    errors.referralCode
                      ? 'border-red-500'
                      : appliedReferral ? 'border-green-500' : ''
                  }`}
                  {...register('referralCode', {
                    validate: (v) =>
                      !v || REF_CODE_RE.test(String(v).trim().toUpperCase())
                      || 'validation.referralInvalid'
                  })}
                />
                {errors.referralCode && (
                  <p className="form-error" role="alert">{t(errors.referralCode.message)}</p>
                )}
                {appliedReferral ? (
                  <p
                    id="referralCode-help"
                    role="status"
                    className="text-xs text-green-700 mt-1 flex items-center gap-1"
                  >
                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span>{t('contact.referralApplied')}</span>
                    <span className="font-mono font-semibold">{appliedReferral}</span>
                  </p>
                ) : (
                  <p
                    id="referralCode-help"
                    className="text-xs text-gray-600 mt-1 border-l-2 border-gray-200 pl-2"
                  >
                    {t('contact.referralHelp')}
                  </p>
                )}
              </div>
            </div>

          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-xl font-semibold text-gray-900">{t('licenses.heading')}</h2>

            {/* Licenses */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('licenses.professionalTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('licenses.professionalHelp')}</p>
              {licenses.map((license, index) => (
                <div key={index} className="p-4 border rounded-lg mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{t('licenses.type')} *</label>
                      <select
                        aria-label={t('licenses.type')}
                        className="form-input"
                        value={license.type}
                        onChange={(e) => {
                          const updated = [...licenses];
                          updated[index].type = e.target.value;
                          setLicenses(updated);
                        }}
                      >
                        <option value="">{t('licenses.selectType')}</option>
                        {/* value is the canonical English string that gets
                            stored and matched server-side; only the label is
                            translated. */}
                        {LICENSE_TYPES.map(licenseType => (
                          <option key={licenseType.key} value={licenseType.value}>
                            {t(`options.licenseTypes.${licenseType.key}`)}
                          </option>
                        ))}
                        <option value="other">{t('options.licenseTypes.other')}</option>
                      </select>
                      {license.type === 'other' && (
                        <input
                          type="text"
                          aria-label={t('licenses.otherTypePlaceholder')}
                          className="form-input mt-2"
                          placeholder={t('licenses.otherTypePlaceholder')}
                          value={license.otherType || ''}
                          onChange={(e) => {
                            const updated = [...licenses];
                            updated[index].otherType = e.target.value;
                            setLicenses(updated);
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <label className="form-label">{t('licenses.number')} *</label>
                      <input
                        type="text"
                        aria-label={t('licenses.number')}
                        className="form-input"
                        value={license.number}
                        onChange={(e) => {
                          const updated = [...licenses];
                          updated[index].number = e.target.value;
                          setLicenses(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('licenses.issuingAuthority')} *</label>
                      <select
                        aria-label={t('licenses.issuingAuthority')}
                        className="form-input"
                        value={license.issuingAuthority}
                        onChange={(e) => {
                          const updated = [...licenses];
                          updated[index].issuingAuthority = e.target.value;
                          setLicenses(updated);
                        }}
                      >
                        <option value="">{t('licenses.selectAuthority')}</option>
                        {US_STATES.map(s => (
                          <option key={s.value} value={s.label}>{s.label}</option>
                        ))}
                        <option value="Federal">{t('licenses.federal')}</option>
                        <option value="other">{t('options.licenseTypes.other')}</option>
                      </select>
                      {license.issuingAuthority === 'other' && (
                        <input
                          type="text"
                          aria-label={t('licenses.otherAuthorityPlaceholder')}
                          className="form-input mt-2"
                          placeholder={t('licenses.otherAuthorityPlaceholder')}
                          value={license.otherAuthority || ''}
                          onChange={(e) => {
                            const updated = [...licenses];
                            updated[index].otherAuthority = e.target.value;
                            setLicenses(updated);
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <label className="form-label">{t('licenses.expiryDate')} *</label>
                      <input
                        type="date"
                        aria-label={t('licenses.expiryDate')}
                        className="form-input"
                        min={today}
                        value={license.expiryDate}
                        onChange={(e) => {
                          const updated = [...licenses];
                          updated[index].expiryDate = e.target.value;
                          setLicenses(updated);
                        }}
                      />
                    </div>
                  </div>
                  {entryErrors.licenses?.[index] && (
                    <p className="form-error mt-2" role="alert">{t(entryErrors.licenses[index])}</p>
                  )}
                  {licenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLicense(index)}
                      className="mt-2 text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-5 w-5 inline mr-1" />
                      {t('documents.remove')}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addLicense}
                className="btn btn-outline btn-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('licenses.add')}
              </button>
            </div>

            {/* Training/Certifications */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('licenses.trainingTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('licenses.trainingHelp')}</p>
              {training.map((cert, index) => (
                <div key={index} className="p-4 border rounded-lg mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">{t('licenses.courseName')}</label>
                      <input
                        type="text"
                        aria-label={t('licenses.courseName')}
                        className="form-input"
                        value={cert.courseName}
                        onChange={(e) => {
                          const updated = [...training];
                          updated[index].courseName = e.target.value;
                          setTraining(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('licenses.institution')}</label>
                      <input
                        type="text"
                        aria-label={t('licenses.institution')}
                        className="form-input"
                        value={cert.institution}
                        onChange={(e) => {
                          const updated = [...training];
                          updated[index].institution = e.target.value;
                          setTraining(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('licenses.completionDate')}</label>
                      <input
                        type="date"
                        aria-label={t('licenses.completionDate')}
                        className="form-input"
                        value={cert.completionDate}
                        onChange={(e) => {
                          const updated = [...training];
                          updated[index].completionDate = e.target.value;
                          setTraining(updated);
                        }}
                      />
                    </div>
                  </div>
                  {entryErrors.training?.[index] && (
                    <p className="form-error mt-2" role="alert">{t(entryErrors.training[index])}</p>
                  )}
                  {training.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTraining(index)}
                      className="mt-2 text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-5 w-5 inline mr-1" />
                      {t('documents.remove')}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTraining}
                className="btn btn-outline btn-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('licenses.addTraining')}
              </button>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-xl font-semibold text-gray-900">{t('skills.heading')}</h2>

            {/* Experience */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('skills.experienceTitle')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">{t('skills.yearsOfExperience')} *</label>
                  <input
                    type="number"
                    aria-label={t('skills.yearsOfExperience')}
                    className={`form-input ${errors.experience?.yearsOfExperience ? 'border-red-500' : ''}`}
                    {...register('experience.yearsOfExperience', {
                      required: 'validation.experienceRequired',
                      min: { value: 0, message: 'validation.experienceNegative' }
                    })}
                  />
                  {errors.experience?.yearsOfExperience && (
                    <p className="form-error" role="alert">{t(errors.experience.yearsOfExperience.message)}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">{t('skills.specializations')}</label>
                  <input
                    type="text"
                    aria-label={t('skills.specializations')}
                    className="form-input"
                    placeholder={t('skills.specializationsPlaceholder')}
                    {...register('experience.specializations')}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('skills.specializationsHelp')}</p>
                </div>
              </div>
            </div>

            {/* Languages */}
            <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('skills.languagesTitle')}</h3>
            {languages.map((lang, index) => (
              <div key={index} className="mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <select
                    aria-label={t('skills.languagesTitle')}
                    className="form-input w-full"
                    value={lang.language}
                    onChange={(e) => {
                      const updated = [...languages];
                      updated[index].language = e.target.value;
                      setLanguages(updated);
                    }}
                  >
                    <option value="">{t('skills.selectLanguage')}</option>
                    {LANGUAGES.map(l => (
                      <option key={l.value} value={l.value}>
                        {t(`options.spokenLanguages.${l.value}`, { defaultValue: l.label })}
                      </option>
                    ))}
                    <option value="other">{t('options.spokenLanguages.other')}</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <select
                    aria-label={t('skills.proficiency')}
                    className="form-input flex-1"
                    value={lang.proficiency}
                    onChange={(e) => {
                      const updated = [...languages];
                      updated[index].proficiency = e.target.value;
                      setLanguages(updated);
                    }}
                  >
                    <option value="basic">{t('options.proficiency.basic')}</option>
                    <option value="conversational">{t('options.proficiency.conversational')}</option>
                    <option value="fluent">{t('options.proficiency.fluent')}</option>
                    <option value="native">{t('options.proficiency.native')}</option>
                  </select>
                  {languages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLanguage(index)}
                      className="text-red-600 hover:text-red-700 px-2"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
              {lang.language === 'other' && (
                <input
                  type="text"
                  aria-label={t('skills.otherLanguagePlaceholder')}
                  className="form-input mt-2"
                  placeholder={t('skills.otherLanguagePlaceholder')}
                  value={lang.otherLanguage || ''}
                  onChange={(e) => {
                    const updated = [...languages];
                    updated[index].otherLanguage = e.target.value;
                    setLanguages(updated);
                  }}
                />
              )}
              {entryErrors.languages?.[index] && (
                <p className="form-error mt-2" role="alert">{t(entryErrors.languages[index])}</p>
              )}
              </div>
            ))}
            <button
              type="button"
              onClick={addLanguage}
              className="btn btn-outline btn-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {t('skills.addLanguage')}
            </button>
          </div>

            {/* Skills checklist */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('skills.checklistTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('skills.checklistHelp')}</p>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('skills.firstAid.certified')}
                  />
                  <span className="ml-2">{t('skills.firstAid')}</span>
                </label>

                {isFirstAidCertified && (
                  <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{t('skills.firstAidProvider')}</label>
                      <input
                        type="text"
                        aria-label={t('skills.firstAidProvider')}
                        className="form-input"
                        {...register('skills.firstAid.provider')}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('skills.firstAidDate')}</label>
                      <input
                        type="date"
                        aria-label={t('skills.firstAidDate')}
                        className="form-input"
                        {...register('skills.firstAid.certificationDate')}
                      />
                    </div>
                  </div>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('skills.weaponsTraining.trained')}
                  />
                  <span className="ml-2">{t('skills.weapons')}</span>
                </label>

                {hasWeaponsTraining && (
                  <div className="ml-6">
                    <label className="form-label">{t('skills.weaponsPermit')}</label>
                    <input
                      type="text"
                      aria-label={t('skills.weaponsPermit')}
                      className="form-input"
                      {...register('skills.weaponsTraining.permitNumber')}
                    />
                  </div>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('personalInfo.militaryService.served')}
                  />
                  <span className="ml-2">{t('skills.military')}</span>
                </label>

                {militaryServed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                    <div>
                      <label className="form-label">{t('skills.militaryBranch')}</label>
                      <input
                        type="text"
                        aria-label={t('skills.militaryBranch')}
                        className="form-input"
                        {...register('personalInfo.militaryService.branch')}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('skills.militaryRank')}</label>
                      <input
                        type="text"
                        aria-label={t('skills.militaryRank')}
                        className="form-input"
                        {...register('personalInfo.militaryService.rank')}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('skills.militaryYears')}</label>
                      <input
                        type="number"
                        aria-label={t('skills.militaryYears')}
                        className="form-input"
                        {...register('personalInfo.militaryService.yearsServed')}
                      />
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          {...register('personalInfo.militaryService.honorableDischarge')}
                        />
                        <span className="ml-2">{t('skills.honorableDischarge')}</span>
                      </label>
                    </div>
                  </div>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('skills.martialArtsTrained')}
                  />
                  <span className="ml-2">{t('skills.martialArts')}</span>
                </label>

                {hasMartialArtsTraining && (
                  <div className="ml-6">
                    {martialArts.map((art, index) => (
                <div key={index} className="p-4 border rounded-lg mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">{t('skills.martialArtsStyle')}</label>
                      <input
                        type="text"
                        aria-label={t('skills.martialArtsStyle')}
                        className="form-input"
                        placeholder={t('skills.martialArtsStylePlaceholder')}
                        value={art.style}
                        onChange={(e) => {
                          const updated = [...martialArts];
                          updated[index].style = e.target.value;
                          setMartialArts(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('skills.martialArtsLevel')}</label>
                      <input
                        type="text"
                        aria-label={t('skills.martialArtsLevel')}
                        className="form-input"
                        placeholder={t('skills.martialArtsLevelPlaceholder')}
                        value={art.level}
                        onChange={(e) => {
                          const updated = [...martialArts];
                          updated[index].level = e.target.value;
                          setMartialArts(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('skills.martialArtsYears')}</label>
                      <input
                        type="number"
                        aria-label={t('skills.martialArtsYears')}
                        className="form-input"
                        value={art.yearsExperience}
                        onChange={(e) => {
                          const updated = [...martialArts];
                          updated[index].yearsExperience = parseInt(e.target.value) || 0;
                          setMartialArts(updated);
                        }}
                      />
                    </div>
                  </div>
                  {entryErrors.martialArts?.[index] && (
                    <p className="form-error mt-2" role="alert">{t(entryErrors.martialArts[index])}</p>
                  )}
                  {martialArts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMartialArt(index)}
                      className="mt-2 text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-5 w-5 inline mr-1" />
                      {t('documents.remove')}
                    </button>
                  )}
                </div>
              ))}
                    <button
                      type="button"
                      onClick={addMartialArt}
                      className="btn btn-outline btn-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      {t('skills.addMartialArt')}
                    </button>
                  </div>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('skills.drivingLicense.hasLicense')}
                  />
                  <span className="ml-2">{t('skills.drivingLicense')}</span>
                </label>

                {hasDriversLicense && (
                  <div className="ml-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        {...register('skills.drivingLicense.defensiveDriving')}
                      />
                      <span className="ml-2">{t('skills.defensiveDriving')}</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Background */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('background.heading')}</h3>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('backgroundCheck.consent', {
                      required: 'validation.consentRequired'
                    })}
                  />
                  {/* Legally operative: this is the consent the admin console
                      hard-gates approval on. Translations of this string and
                      background.criminalRecordHelp are flagged for review. */}
                  <span className="ml-2">{t('background.consent')}</span>
                </label>
                <p className="text-xs text-gray-500 ml-6">{t('background.consentHelp')}</p>
                {errors.backgroundCheck?.consent && (
                  <p className="form-error" role="alert">{t(errors.backgroundCheck.consent.message)}</p>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    {...register('backgroundCheck.criminalRecord.hasCriminalRecord')}
                  />
                  <span className="ml-2">{t('background.criminalRecord')}</span>
                </label>
                <p className="text-xs text-gray-500 ml-6">{t('background.criminalRecordHelp')}</p>

                {hasCriminalRecord && (
                  <div className="ml-6">
                    <label className="form-label">{t('background.criminalRecordDetails')}</label>
                    <textarea
                      aria-label={t('background.criminalRecordDetails')}
                      className="form-input"
                      rows={3}
                      {...register('backgroundCheck.criminalRecord.details')}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Insurance */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('insurance.heading')}</h3>
              <p className="form-label">{t('insurance.question')} *</p>
              <div className="flex gap-6 mt-1">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="yes"
                    className="form-radio"
                    {...register('insurance.hasLiabilityInsurance', {
                      required: 'validation.insuranceRequired'
                    })}
                  />
                  <span className="ml-2">{t('insurance.yes')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="no"
                    className="form-radio"
                    {...register('insurance.hasLiabilityInsurance', {
                      required: 'validation.insuranceRequired'
                    })}
                  />
                  <span className="ml-2">{t('insurance.no')}</span>
                </label>
              </div>
              {errors.insurance?.hasLiabilityInsurance && (
                <p className="form-error" role="alert">{t(errors.insurance.hasLiabilityInsurance.message)}</p>
              )}
              {insuranceAnswer === 'no' && (
                <p className="text-xs text-gray-500 mt-2">{t('insurance.noHelp')}</p>
              )}
            </div>

            {/* Availability */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('availability.heading')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">{t('availability.startDate')} *</label>
                  <input
                    type="date"
                    aria-label={t('availability.startDate')}
                    min={today}
                    className={`form-input ${errors.availability?.startDate ? 'border-red-500' : ''}`}
                    {...register('availability.startDate', {
                      required: 'validation.startDateRequired',
                      validate: (v) => v >= today || 'validation.startDateInPast'
                    })}
                  />
                  {errors.availability?.startDate && (
                    <p className="form-error" role="alert">{t(errors.availability.startDate.message)}</p>
                  )}
                </div>
              </div>

              <fieldset className="mt-6">
                <legend className="form-label">{t('availability.workType')}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      {...register('availability.fullTime')}
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('availability.fullTime')}</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      {...register('availability.partTime')}
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('availability.partTime')}</span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="mt-6">
                <legend className="form-label">{t('availability.additional')}</legend>
                <p className="text-xs text-gray-500 mb-2">{t('availability.additionalHelp')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      {...register('availability.nightShifts')}
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('availability.nightShifts')}</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      {...register('availability.weekends')}
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('availability.weekends')}</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      {...register('availability.willingToTravel')}
                    />
                    <span className="ml-2 text-sm text-gray-900">{t('availability.willingToTravel')}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            {/* Anything else */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('additionalInfo.heading')}</h3>
              <label className="form-label">{t('additionalInfo.label')}</label>
              <textarea
                aria-label={t('additionalInfo.heading')}
                className="form-input"
                rows={4}
                {...register('additionalInfo')}
              />
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-xl font-semibold text-gray-900">{t('documents.heading')}</h2>

            {/* Document Upload */}
            <div className="space-y-4">
            <p className="text-sm text-gray-600">{t('documents.intro')}</p>
            <p className="text-sm text-gray-500">{t('documents.security')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{t('documents.governmentId')} *</label>
                <input
                  type="file"
                  aria-label={t('documents.governmentId')}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'id')}
                  className="form-input"
                />
                {files.id && (
                  <div className="mt-2 flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-600 truncate max-w-[200px]">{files.id.name}</span>
                    <button type="button" aria-label={t('documents.remove')} onClick={() => removeFile('id', 0)} className="text-red-600 hover:text-red-700">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('documents.hintSingle')}</p>
              </div>

              <div>
                <label className="form-label">{t('documents.licenses')}</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  aria-label={t('documents.licenses')}
                  onChange={(e) => handleFileChange(e, 'license')}
                  className="form-input"
                  multiple
                />
                {files.license.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.license.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-xs text-gray-600 truncate max-w-[200px]">{f.name}</span>
                        <button type="button" aria-label={t('documents.remove')} onClick={() => removeFile('license', idx)} className="text-red-600 hover:text-red-700">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('documents.hintMultiple')}</p>
              </div>

              <div>
                <label className="form-label">{t('documents.certifications')}</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  aria-label={t('documents.certifications')}
                  onChange={(e) => handleFileChange(e, 'certification')}
                  className="form-input"
                  multiple
                />
                {files.certification.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.certification.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-xs text-gray-600 truncate max-w-[200px]">{f.name}</span>
                        <button type="button" aria-label={t('documents.remove')} onClick={() => removeFile('certification', idx)} className="text-red-600 hover:text-red-700">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('documents.hintMultiple')}</p>
              </div>

              <div>
                <label className="form-label">{t('documents.other')}</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  aria-label={t('documents.other')}
                  onChange={(e) => handleFileChange(e, 'other')}
                  className="form-input"
                  multiple
                />
                {files.other.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.other.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-xs text-gray-600 truncate max-w-[200px]">{f.name}</span>
                        <button type="button" aria-label={t('documents.remove')} onClick={() => removeFile('other', idx)} className="text-red-600 hover:text-red-700">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('documents.hintOther')}</p>
              </div>
            </div>
          </div>

            {/* Next: review */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-medium text-green-900 mb-2">{t('documents.almostDoneTitle')}</h3>
              <p className="text-sm text-green-700">{t('documents.almostDoneBody')}</p>
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <ApplicationReview
              values={formValues}
              licenses={licenses}
              training={training}
              languages={languages}
              martialArts={martialArts}
              files={files}
              onEdit={(step) => setCurrentStep(step)}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-lg shadow-sm max-w-md w-full text-center"
          role="status"
          aria-live="polite"
        >
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('success.title')}</h2>
          <p className="text-gray-600 mb-6">{t('success.body')}</p>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-500">{t('success.referenceId')}</p>
            <p className="text-lg font-mono font-bold text-gray-900">{applicationId}</p>
          </div>
          {myReferralCode ? (
            <div className="bg-primary-50 p-4 rounded-lg text-left">
              <p className="text-sm text-gray-700 mb-3">{t('success.referralIntro')}</p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="bg-white p-2 rounded-lg border border-gray-200">
                  <QRCodeSVG
                    value={`${window.location.origin}/apply?ref=${myReferralCode}`}
                    size={128}
                    marginSize={1}
                    aria-label={t('success.qrAlt')}
                  />
                </div>
                <div className="flex-1 w-full">
                  <p className="text-xs text-gray-500">{t('success.yourCode')}</p>
                  <p className="text-xl font-mono font-bold text-gray-900 mb-2">{myReferralCode}</p>
                  <p className="text-xs text-gray-500">{t('success.yourLink')}</p>
                  <p className="text-sm font-mono text-gray-900 break-all mb-3">
                    {`${window.location.origin}/apply?ref=${myReferralCode}`}
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(`${window.location.origin}/apply?ref=${myReferralCode}`)
                        .then(() => toast.success(t('success.copied')))
                        .catch(() => toast.error(t('success.copyFailed')));
                    }}
                  >
                    {t('success.copyLink')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-primary-50 p-4 rounded-lg text-left">
              <p className="text-sm text-gray-700">{t('success.fallbackIntro')}</p>
              <a
                href="https://www.guardly.app/apply"
                className="mt-2 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                guardly.app/apply
              </a>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // Ahead of the intro: a returning visitor decides what happens to the saved
  // draft before any of its contents are rendered.
  if (showResume) {
    return (
      <ApplicationResume
        onContinue={handleResume}
        onStartFresh={handleStartFresh}
        savedStep={currentStep}
        totalSteps={totalSteps}
      />
    );
  }

  if (showIntro) {
    return (
      <ApplicationIntro
        onNext={() => {
          setShowIntro(false);
          window.scrollTo({ top: 0 });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stays reachable on every step: someone who started in English may
            hit a section they can't read and needs the switch right there,
            without losing what they have already filled in. */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <ShieldCheckIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" aria-hidden="true" />
          <h1 className="text-3xl font-display font-bold text-gray-900">
            {t('header.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('header.subtitle')}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEP_LABEL_KEYS.map((_, i) => i + 1).map((step) => (
              <div
                key={step}
                className={`flex items-center ${step < totalSteps ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step <= currentStep
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                {step < totalSteps && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            {STEP_LABEL_KEYS.map((key) => (
              <span key={key}>{t(key)}</span>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-sm p-6">
          {renderStep()}

          {submitError && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
              <p className="text-sm font-medium text-red-800">{submitError}</p>
              <p className="text-sm text-red-700 mt-1">{t('errors.submitTitle')}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            <button
              type="button"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              className={`btn btn-outline ${currentStep === 1 ? 'invisible' : ''}`}
              disabled={currentStep === 1}
            >
              {t('nav.previous')}
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn btn-primary"
              >
                {t('nav.next')}
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    {t('nav.submitting')}
                  </>
                ) : (
                  t('nav.submit')
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationForm;
